/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast, Toaster } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    CheckCircle, XCircle, Trash2, Loader2, AlertCircle,
    User, Phone, Mail, Building, X, Eye, FileText, Download,
    Users, TrendingUp, Clock, Ban, PlusCircle, List, Bell
} from 'lucide-react';
import api from '../../services/api';
import useAuth from '../../hooks/useAuth';

const API_BASE = import.meta.env.VITE_API_BASE_URL_NEW;
const STORAGE_URL = import.meta.env.STORAGE_URL;

const StatCard = ({ label, value, color, icon: Icon, loading }) => (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-5 flex items-center gap-4`}>
        <div className={`p-3 rounded-lg ${color}`}>
            <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
            <p className="text-sm text-gray-500">{label}</p>
            {loading ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400 mt-1" />
            ) : (
                <p className="text-2xl font-bold text-gray-900">{value ?? 0}</p>
            )}
        </div>
    </div>
);

export default function Dashboard() {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();

    // CRM stats
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);

    // Referrals (existing)
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [updatingId, setUpdatingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [selectedReferral, setSelectedReferral] = useState(null);

    // Tasks / Follow-up reminders
    const [pendingTasks, setPendingTasks] = useState([]);
    const [tasksLoading, setTasksLoading] = useState(true);

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const totalPages = Math.ceil(referrals.length / itemsPerPage);
    const paginatedReferrals = referrals.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    useEffect(() => {
        const token = localStorage.getItem('admin_token');
        if (!token) {
            setError('login-required');
            setLoading(false);
            setStatsLoading(false);
            setTasksLoading(false);
            return;
        }

        // Fetch CRM stats
        api.get('/crm/dashboard-stats')
            .then(res => setStats(res.data))
            .catch(() => setStats(null))
            .finally(() => setStatsLoading(false));

        // Fetch referrals
        if (isAdmin) {
            api.get('/referrals')
                .then(res => setReferrals(res.data.data || []))
                .catch(err => setError(err.response?.data?.message || 'Failed to load referrals'))
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }

        // Fetch pending tasks (follow-up reminders)
        api.get('/tasks', { params: { is_completed: false } })
            .then(res => setPendingTasks((res.data || []).slice(0, 5)))
            .catch(() => setPendingTasks([]))
            .finally(() => setTasksLoading(false));
    }, [isAdmin]);

    const handleUpdateStatus = async (id, newStatus) => {
        setUpdatingId(id);
        try {
            await api.patch(`/referrals/${id}/status`, { status: newStatus });
            setReferrals(prev => prev.map(r => (r.id === id ? { ...r, status: newStatus } : r)));
            toast.success(`Referral ${newStatus === 'accepted' ? 'accepted' : 'rejected'} successfully`);
        } catch {
            toast.error('Failed to update status');
        } finally {
            setUpdatingId(null);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this referral?')) return;
        setDeletingId(id);
        try {
            await api.delete(`/referrals/${id}`);
            setReferrals(prev => prev.filter(r => r.id !== id));
            toast.success('Referral deleted successfully');
        } catch {
            toast.error('Failed to delete referral');
        } finally {
            setDeletingId(null);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
            accepted: 'bg-green-100 text-green-800 border-green-300',
            rejected: 'bg-red-100 text-red-800 border-red-300',
        };
        return (
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium border ${styles[status] || 'bg-gray-100'}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const getFileUrl = (path) => {
        if (!path) return null;
        return `https://api.westlake.saasbizhub.com/storage/${path}`;
    };

    if (error === 'login-required') {
        return (
            <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-6 space-y-6">
                <AlertCircle className="w-16 h-16 text-red-500" />
                <p className="text-xl font-semibold text-gray-800">You need to be logged in to view this page</p>
                <button
                    onClick={() => window.location.href = '/admin/auth/login'}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-md transition"
                >
                    Go to Login
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <Toaster position="top-right" richColors closeButton />

            <div className="max-w-7xl mx-auto space-y-10">
                {/* Header */}
                <div>
                    <h2 className="text-3xl font-bold text-gray-900">Dashboard</h2>
                    <p className="text-gray-500 mt-1">Welcome back. Here's what's happening today.</p>
                </div>

                {/* CRM Lead Stats */}
                <section>
                    <h2 className="text-lg font-semibold text-gray-700 mb-4">Lead Overview</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        <StatCard label="Total Leads" value={stats?.total} color="bg-blue-600" icon={Users} loading={statsLoading} />
                        <StatCard label="New Today" value={stats?.new_today} color="bg-green-600" icon={TrendingUp} loading={statsLoading} />
                        <StatCard label="Pending Follow-up" value={stats?.pending_followup} color="bg-yellow-500" icon={Clock} loading={statsLoading} />
                        <StatCard label="Converted" value={stats?.converted} color="bg-emerald-600" icon={CheckCircle} loading={statsLoading} />
                        <StatCard label="Lost" value={stats?.lost} color="bg-red-500" icon={Ban} loading={statsLoading} />
                    </div>
                </section>

                {/* Quick Actions */}
                <section>
                    <h2 className="text-lg font-semibold text-gray-700 mb-4">Quick Actions</h2>
                    <div className="flex flex-wrap gap-3">
                        <button
                            onClick={() => navigate('/admin/leads/new')}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition shadow-sm"
                        >
                            <PlusCircle className="w-4 h-4" />
                            Add New Lead
                        </button>
                        <button
                            onClick={() => navigate('/admin/leads')}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition shadow-sm"
                        >
                            <List className="w-4 h-4" />
                            View Recent Leads
                        </button>
                        <button
                            onClick={() => navigate('/admin/tasks')}
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition shadow-sm"
                        >
                            <Bell className="w-4 h-4" />
                            Follow-Up Reminders
                            {pendingTasks.length > 0 && (
                                <span className="ml-1 inline-flex items-center justify-center w-5 h-5 bg-red-500 text-white text-xs rounded-full">
                                    {pendingTasks.length}
                                </span>
                            )}
                        </button>
                    </div>
                </section>

                {/* Pending Follow-Up Tasks */}
                {!tasksLoading && pendingTasks.length > 0 && (
                    <section>
                        <h2 className="text-lg font-semibold text-gray-700 mb-4">Upcoming Follow-Ups</h2>
                        <div className="bg-white rounded-xl border border-gray-200 shadow-sm divide-y divide-gray-100">
                            {pendingTasks.map(task => (
                                <div key={task.id} className="flex items-center justify-between px-5 py-3 hover:bg-gray-50">
                                    <div>
                                        <p className="font-medium text-gray-800">{task.title}</p>
                                        <p className="text-sm text-gray-500">
                                            {task.lead?.name ? `Lead: ${task.lead.name}` : 'No linked lead'}
                                            {task.due_date ? ` · Due: ${new Date(task.due_date).toLocaleDateString()}` : ''}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => navigate('/admin/tasks')}
                                        className="text-sm text-blue-600 hover:underline"
                                    >
                                        View
                                    </button>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Referrals Table - Admins Only */}
                {isAdmin && (
                    <section>
                        <h2 className="text-lg font-semibold text-gray-700 mb-4">Patient Referrals</h2>
                        {loading ? (
                            <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>
                        ) : (
                            <div className="bg-white shadow-lg rounded-xl overflow-hidden border border-gray-200 overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Patient</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Referrer</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Location</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Date</th>
                                            <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Status</th>
                                            <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {paginatedReferrals.map((ref) => (
                                            <tr key={ref.id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4">
                                                    <div className="font-medium">{ref.patient_first_name} {ref.patient_last_name}</div>
                                                    <div className="text-sm text-gray-500">DOB: {new Date(ref.patient_dob).toLocaleDateString()}</div>
                                                </td>
                                                <td className="px-6 py-4 text-sm">
                                                    {ref.referrer_first_name} {ref.referrer_last_name}
                                                    <div className="text-gray-500">{ref.referrer_email}</div>
                                                </td>
                                                <td className="px-6 py-4">{ref.current_location}</td>
                                                <td className="px-6 py-4 text-sm text-gray-500">{new Date(ref.created_at).toLocaleString()}</td>
                                                <td className="px-6 py-4">{getStatusBadge(ref.status)}</td>
                                                <td className="px-6 py-4 text-center">
                                                    <div className="flex justify-center gap-3">
                                                        <button onClick={() => setSelectedReferral(ref)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="View Details">
                                                            <Eye className="w-5 h-5" />
                                                        </button>
                                                        {ref.status === 'pending' && (
                                                            <>
                                                                <button onClick={() => handleUpdateStatus(ref.id, 'accepted')} disabled={updatingId === ref.id} className="p-2 text-green-600 hover:bg-green-50 rounded-lg disabled:opacity-50">
                                                                    <CheckCircle className="w-5 h-5" />
                                                                </button>
                                                                <button onClick={() => handleUpdateStatus(ref.id, 'rejected')} disabled={updatingId === ref.id} className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50">
                                                                    <XCircle className="w-5 h-5" />
                                                                </button>
                                                            </>
                                                        )}
                                                        <button onClick={() => handleDelete(ref.id)} disabled={deletingId === ref.id} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50">
                                                            {deletingId === ref.id ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {totalPages > 1 && (
                                    <div className="px-6 py-4 border-t flex justify-between items-center">
                                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} className="px-4 py-2 border rounded disabled:opacity-50">Previous</button>
                                        <span>Page {currentPage} of {totalPages}</span>
                                        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} className="px-4 py-2 border rounded disabled:opacity-50">Next</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </section>
                )}
            </div>

            {/* Referral Detail Modal */}
            <AnimatePresence>
                {selectedReferral && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedReferral(null)} className="absolute inset-0 bg-black/60" />
                        <motion.div initial={{ scale: 0.9, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.9, opacity: 0, y: 20 }} className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
                                <h2 className="text-2xl font-bold text-gray-900">Referral Details</h2>
                                <button onClick={() => setSelectedReferral(null)} className="p-2 hover:bg-gray-100 rounded-full transition"><X className="w-6 h-6" /></button>
                            </div>
                            <div className="p-6 space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <h3 className="font-semibold text-lg mb-2">Patient Information</h3>
                                        <p><strong>Name:</strong> {selectedReferral.patient_first_name} {selectedReferral.patient_last_name}</p>
                                        <p><strong>Date of Birth:</strong> {new Date(selectedReferral.patient_dob).toLocaleDateString()}</p>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg mb-2">Referrer Information</h3>
                                        <p><strong>Name:</strong> {selectedReferral.referrer_first_name} {selectedReferral.referrer_last_name}</p>
                                        <p><strong>Email:</strong> {selectedReferral.referrer_email}</p>
                                        <p><strong>Phone:</strong> {selectedReferral.referrer_phone}</p>
                                        <p><strong>Professional:</strong> {selectedReferral.is_healthcare_professional}</p>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-lg mb-2">Referral Details</h3>
                                        <p><strong>Current Location:</strong> {selectedReferral.current_location}</p>
                                        <p><strong>Insurance #:</strong> {selectedReferral.insurance_number || '—'}</p>
                                        <p><strong>Status:</strong> {getStatusBadge(selectedReferral.status)}</p>
                                        <p><strong>Created:</strong> {new Date(selectedReferral.created_at).toLocaleString()}</p>
                                    </div>
                                </div>
                                {selectedReferral.insurance_card_path && (
                                    <div className="mt-6 border-t pt-6">
                                        <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                                            <FileText className="w-5 h-5 text-blue-600" /> Insurance Card
                                        </h3>
                                        <div className="flex flex-col sm:flex-row gap-4 items-start">
                                            <a href={getFileUrl(selectedReferral.insurance_card_path)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors">
                                                <Download className="w-4 h-4" /> View / Download Insurance Card
                                            </a>
                                            {selectedReferral.insurance_card_path.toLowerCase().match(/\.(jpg|jpeg|png)$/i) && (
                                                <img src={getFileUrl(selectedReferral.insurance_card_path)} alt="Insurance Card Preview" className="max-w-full sm:max-w-xs rounded-lg shadow-md object-contain border" onError={(e) => { e.target.style.display = 'none'; }} />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end">
                                <button onClick={() => setSelectedReferral(null)} className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition">Close</button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}